import React, { useEffect, useRef, useState } from "react";
import { format, parseISO } from "date-fns";
import {
  CircularProgress,
  IconButton,
  InputBase,
  Paper,
  Tooltip,
  makeStyles,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import NavigateBeforeIcon from "@material-ui/icons/NavigateBefore";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import SearchIcon from "@material-ui/icons/Search";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const PAGE_SIZE = 20;

const useStyles = makeStyles((theme) => ({
  root: {
    position: "absolute",
    zIndex: 7,
    top: 10,
    right: 14,
    width: "min(480px, calc(100% - 28px))",
  },
  toggle: {
    position: "absolute",
    zIndex: 6,
    top: 10,
    right: 14,
    color: "#52616b",
    background: "rgba(255,255,255,0.94)",
    boxShadow: "0 2px 8px rgba(15,23,42,0.12)",
    "&:hover": { background: "#fff" },
  },
  toolbar: {
    minHeight: 46,
    display: "flex",
    alignItems: "center",
    gap: 4,
    padding: "4px 8px 4px 14px",
    borderRadius: 24,
    boxShadow: "0 2px 10px rgba(15,23,42,0.16)",
  },
  searchIcon: { color: "#73808a", fontSize: 20, flex: "none" },
  input: { flex: 1, minWidth: 0, fontSize: 14 },
  resultCount: {
    flex: "none",
    color: theme.palette.text.secondary,
    fontSize: 12,
    whiteSpace: "nowrap",
  },
  results: {
    marginTop: 8,
    maxHeight: 330,
    overflowY: "auto",
    borderRadius: 12,
    boxShadow: "0 4px 18px rgba(15,23,42,0.18)",
  },
  result: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    padding: "10px 14px",
    border: 0,
    borderBottom: "1px solid rgba(0,0,0,0.07)",
    background: "#fff",
    textAlign: "left",
    cursor: "pointer",
    "&:hover, &:focus-visible": { background: "#f2f6f8", outline: "none" },
    "&:last-child": { borderBottom: 0 },
  },
  resultMeta: {
    color: "#64727c",
    fontSize: 11,
    marginBottom: 3,
  },
  resultBody: {
    color: "#263238",
    fontSize: 13,
    lineHeight: 1.4,
    overflow: "hidden",
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: 2,
    overflowWrap: "anywhere",
  },
  empty: {
    padding: "14px 16px",
    color: "#64727c",
    fontSize: 13,
  },
  pagination: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    padding: "4px 8px",
    borderTop: "1px solid rgba(0,0,0,0.07)",
    color: "#64727c",
    fontSize: 12,
  },
}));

const MessageHistorySearch = ({ ticketId, onSelectResult }) => {
  const classes = useStyles();
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [results, setResults] = useState([]);
  const [count, setCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    const searchParam = query.trim();
    if (!open || searchParam.length < 2) {
      setResults([]);
      setCount(0);
      setHasMore(false);
      setLoading(false);
      return undefined;
    }

    let active = true;
    const timeout = setTimeout(() => {
      setLoading(true);
      api
        .get(`/messages/${ticketId}/search`, {
          params: { searchParam, pageNumber: page },
        })
        .then(({ data }) => {
          if (!active) return;
          setResults(data.results || []);
          setCount(data.count || 0);
          setHasMore(Boolean(data.hasMore));
        })
        .catch((error) => {
          if (active) toastError(error);
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    }, 250);

    return () => {
      active = false;
      clearTimeout(timeout);
    };
  }, [open, query, page, ticketId]);

  const close = () => {
    setOpen(false);
    setQuery("");
    setPage(1);
    setResults([]);
    setCount(0);
  };

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  return (
    <>
      {!open && (
        <Tooltip title="Buscar nesta conversa">
          <IconButton
            className={classes.toggle}
            size="small"
            aria-label="Buscar mensagens nesta conversa"
            onClick={() => setOpen(true)}
          >
            <SearchIcon />
          </IconButton>
        </Tooltip>
      )}
      {open && (
        <div className={classes.root}>
          <Paper className={classes.toolbar} elevation={0}>
            <SearchIcon className={classes.searchIcon} />
            <InputBase
              inputRef={inputRef}
              className={classes.input}
              value={query}
              placeholder="Buscar nesta conversa"
              inputProps={{
                "aria-label": "Buscar mensagens nesta conversa",
                onKeyDown: (event) => {
                  if (event.key === "Escape") close();
                },
              }}
              onChange={(event) => {
                setQuery(event.target.value);
                setPage(1);
              }}
            />
            {loading ? (
              <CircularProgress size={16} />
            ) : query.trim().length >= 2 ? (
              <span className={classes.resultCount}>{count} resultados</span>
            ) : null}
            <Tooltip title="Fechar busca">
              <IconButton size="small" aria-label="Fechar busca" onClick={close}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Paper>
          {query.trim().length >= 2 && !loading && (
            <Paper className={classes.results} elevation={0} role="listbox">
              {results.length ? (
                results.map(({ message, historyPage }) => {
                  const body = String(message.body || "")
                    .replace(/^\u200e+/, "")
                    .trim();
                  return (
                    <button
                      type="button"
                      role="option"
                      aria-selected="false"
                      key={message.id}
                      className={classes.result}
                      onClick={() => {
                        onSelectResult({ message, historyPage });
                        close();
                      }}
                    >
                      <span className={classes.resultMeta}>
                        {message.fromMe
                          ? "Você"
                          : message.contact?.name || "Contato"} — {format(parseISO(message.createdAt), "dd/MM/yyyy HH:mm")}
                      </span>
                      <span className={classes.resultBody}>
                        {body || `Mensagem de ${message.mediaType || "mídia"}`}
                      </span>
                    </button>
                  );
                })
              ) : (
                <div className={classes.empty}>Nenhuma mensagem encontrada.</div>
              )}
              {count > PAGE_SIZE && (
                <div className={classes.pagination}>
                  <IconButton
                    size="small"
                    aria-label="Resultados anteriores"
                    disabled={page <= 1 || loading}
                    onClick={() => setPage((current) => Math.max(1, current - 1))}
                  >
                    <NavigateBeforeIcon />
                  </IconButton>
                  <span>
                    Página {page} de {totalPages}
                  </span>
                  <IconButton
                    size="small"
                    aria-label="Próximos resultados"
                    disabled={!hasMore || loading}
                    onClick={() => setPage((current) => current + 1)}
                  >
                    <NavigateNextIcon />
                  </IconButton>
                </div>
              )}
            </Paper>
          )}
        </div>
      )}
    </>
  );
};

export default MessageHistorySearch;